import { INTERSTITIAL_EXPIRATION_TYPE, INTERSTITIAL_TYPE_ID } from '@mgCommon/constants/constants.js';
import { convertEpochToReadableDate, decodeUrl } from '@mgCommon/utils/utils.js';
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  titleGr: '',
  titleEn: '',
  templateGr: '',
  templateEn: '',
  backgroundImage: '',
  name: '',
  position: 'center',
  backgroundColor: '#ffffff',
  defaultColor: '#000000',
  size: 'medium',
  closeButton: true,
  expirationTimer: 0,
  priority: 6,
  animation: '',
  animationSpawn: '',
  game: '',
  expirationType: '',
  expirationDate: '',
  expirationInDays: '',
  expirationInDaysHour: '',
  expirationInHours: '',
  interstitialType: INTERSTITIAL_TYPE_ID.RICH,
  hasHyperLink: false,
  interstitialsMode: 'modal',
  closeButtonColor: '#000000',
  eligibleUrl: '',
};

const interstitialFormDataSlice = createSlice({
  name: 'interstitialFormData',
  initialState,
  reducers: {
    setInterstitialFormData(state, { payload }) {
      const { expirationDate, expirationType } = payload;
      if (expirationDate && !expirationType) {
        state.expirationType = INTERSTITIAL_EXPIRATION_TYPE.DATE;
      } else {
        state.expirationType = expirationType;
      }
      state.titleGr = payload.titleGr;
      state.titleEn = payload.titleEn;
      state.templateGr = payload.templateGr;
      state.templateEn = payload.templateEn;
      state.backgroundImage = payload.backgroundImage;
      state.name = payload.name;
      state.position = payload.position;
      state.backgroundColor = payload.backgroundColor;
      state.defaultColor = payload.defaultColor;
      state.size = payload.size;
      state.closeButton = payload.closeButton;
      state.expirationTimer = Number(payload.expirationTimer);
      state.priority = Number(payload.priority);
      state.animation = payload.animation;
      state.animationSpawn = payload.animationSpawn;
      state.game = payload.game;
      state.expirationDate = convertEpochToReadableDate({ epoch: expirationDate });
      state.expirationInDays = payload.expirationInDays ?? '';
      state.expirationInDaysHour = payload.expirationInDaysHour ?? '';
      state.expirationInHours = payload.expirationInHours ?? '';
      state.interstitialType = payload.interstitialType;
      state.hasHyperLink = payload.hasHyperLink;
      state.interstitialsMode = payload.interstitialsMode;
      state.closeButtonColor = payload.closeButtonColor;
      state.eligibleUrl = decodeUrl(payload.eligibleUrl);
    },
    setInterstitialTitleGr(state, { payload }) {
      state.titleGr = payload.titleGr;
    },
    setInterstitialTitleEn(state, { payload }) {
      state.titleEn = payload.titleEn;
    },
    setInterstitialTemplateGr(state, { payload }) {
      state.templateGr = payload.templateGr;
    },
    setInterstitialTemplateEn(state, { payload }) {
      state.templateEn = payload.templateEn;
    },
    setInterstitialBackgroundImage(state, { payload }) {
      state.backgroundImage = payload.backgroundImage;
    },
    setInterstitialName(state, { payload }) {
      state.name = payload.name;
    },
    setInterstitialPosition(state, { payload }) {
      state.position = payload.position;
    },
    setInterstitialBackgroundColor(state, { payload }) {
      state.backgroundColor = payload.backgroundColor;
    },
    setInterstitialDefaultColor(state, { payload }) {
      state.defaultColor = payload.defaultColor;
    },
    setInterstitialSize(state, { payload }) {
      state.size = payload.size;
    },
    setInterstitialHasCloseButton(state, { payload }) {
      state.closeButton = payload.closeButton;
    },
    setInterstitialExpirationTimer(state, { payload }) {
      state.expirationTimer = Number(payload.expirationTimer);
    },
    setInterstitialPriority(state, { payload }) {
      state.priority = Number(payload.priority);
    },
    setInterstitialAnimation(state, { payload }) {
      state.animation = payload.animation;
    },
    setInterstitialAnimationSpawn(state, { payload }) {
      state.animationSpawn = payload.animationSpawn;
    },
    setInterstitialGameId(state, { payload }) {
      state.game = payload.game;
    },
    setInterstitialExpirationType(state, { payload }) {
      const { expirationType } = payload;
      state.expirationType = expirationType;

      switch (expirationType) {
        case INTERSTITIAL_EXPIRATION_TYPE.DATE:
          state.expirationInDays = '';
          state.expirationInDaysHour = '';
          state.expirationInHours = '';
          break;
        case INTERSTITIAL_EXPIRATION_TYPE.IN_DAYS:
          state.expirationDate = '';
          state.expirationInHours = '';
          break;
        case INTERSTITIAL_EXPIRATION_TYPE.IN_HOURS:
          state.expirationDate = '';
          state.expirationInDays = '';
          state.expirationInDaysHour = '';
          break;
        default:
          state.expirationDate = '';
          state.expirationInDays = '';
          state.expirationInDaysHour = '';
          state.expirationInHours = '';
          break;
      }
    },
    setInterstitialExpirationDate(state, { payload }) {
      state.expirationDate = payload.expirationDate;
    },
    setInterstitialExpirationInDays(state, { payload }) {
      state.expirationInDays = Number(payload.expirationInDays);
    },
    setInterstitialExpirationInDaysHour(state, { payload }) {
      state.expirationInDaysHour = payload.expirationInDaysHour;
    },
    setInterstitialExpirationInHours(state, { payload }) {
      state.expirationInHours = Number(payload.expirationInHours);
    },
    setInterstitialType(state, { payload }) {
      state.interstitialType = payload.interstitialType;
    },
    setInterstitialHasHyperLink(state, { payload }) {
      state.hasHyperLink = payload.hasHyperLink;
    },
    setInterstitialsMode(state, { payload }) {
      state.interstitialsMode = payload.interstitialsMode;
    },
    setInterstitialCloseButtonColor(state, { payload }) {
      state.closeButtonColor = payload.closeButtonColor;
    },
    setInterstitialsEligibleUrl(state, { payload }) {
      const localeSanitizedEligibleUrl = payload.eligibleUrl.replace(/(\/en\/)|(\/el\/)/i, '/');
      state.eligibleUrl = localeSanitizedEligibleUrl;
    },
  },
});

export const {
  setInterstitialFormData,
  setInterstitialTitleGr,
  setInterstitialTitleEn,
  setInterstitialTemplateGr,
  setInterstitialTemplateEn,
  setInterstitialBackgroundImage,
  setInterstitialName,
  setInterstitialPosition,
  setInterstitialBackgroundColor,
  setInterstitialDefaultColor,
  setInterstitialSize,
  setInterstitialHasCloseButton,
  setInterstitialExpirationTimer,
  setInterstitialPriority,
  setInterstitialAnimation,
  setInterstitialAnimationSpawn,
  setInterstitialGameId,
  setInterstitialExpirationType,
  setInterstitialExpirationDate,
  setInterstitialExpirationInDays,
  setInterstitialExpirationInDaysHour,
  setInterstitialExpirationInHours,
  setInterstitialType,
  setInterstitialHasHyperLink,
  setInterstitialsMode,
  setInterstitialCloseButtonColor,
  setInterstitialsEligibleUrl,
} = interstitialFormDataSlice.actions;

export default interstitialFormDataSlice.reducer;
